import { SelectMenu, Table, TableColumn, Typography } from '@scale/scaleui';
import { Link } from 'react-router-dom';
import { Bar, BarChart, Tooltip, XAxis } from 'recharts';

import { TableColumnDisplayOrderConfig } from 'react-scale/corp/components/UI/TableColumnDisplayOrder/TableColumnDisplayOrderConfig';
import { useTableColumnDisplayOrder } from 'react-scale/corp/components/UI/TableColumnDisplayOrder/useTableColumnDisplayOrder';

import { SquadType } from '@scale/shared/types/expertManagement';
import { useCallback, useEffect, useMemo } from 'react';
import {
  AUTO_HIDEABLE_COLUMNS,
  SQUAD_TYPE_TO_NAME_MAP,
} from 'react-scale/components/ExpertManagement/constants';
import { useCanUserModifyCommunity } from 'react-scale/components/ExpertManagement/hooks/useCanUserModifyCommunity';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import { CellFnProps, Rounded } from 'react-scale/components/table/cellFunctions';
import { useExpertActionsState } from '../hooks/useExpertActionsState';
import { LoadingTable } from './LoadingTable';
import { MoveToNewManagerModal } from './MoveToNewManagerModal';

function Stats(stats: any, options?: { percentage?: boolean; decimalPlaces?: number }) {
  if (!stats) {
    return 'No data available.';
  }
  const { average, coverage } = stats;
  if (!average) {
    return 'No data available.';
  }
  return (
    <div>
      <div className="text-sm">
        <span className="font-medium">
          {average.toFixed(options?.decimalPlaces ?? 0)}
          {options?.percentage && '%'}
        </span>
      </div>
      <div className="text-sm">{(coverage * 100).toFixed(0)}% coverage</div>
    </div>
  );
}

function BarDistribution(props: CellFnProps) {
  const data = props.getValue();
  return (
    <BarChart barGap={0} width={200} height={80} data={data}>
      <XAxis type="category" dataKey="stars" tickFormatter={t => `${t}☆`} />
      <Tooltip
        offset={0}
        position={{ x: 0, y: -120 }}
        allowEscapeViewBox={{ x: true, y: true }}
        isAnimationActive={false}
        labelFormatter={t => `${t}-star ratings`}
        labelStyle={{ padding: 0, margin: 0, fontWeight: '700' }}
        itemStyle={{ padding: 0, margin: 0, fontWeight: '600' }}
        formatter={(t: any) => `${(parseFloat(t) * 100).toFixed(0)}%`}
        contentStyle={{ boxShadow: '0 0 5px 0 rgba(0, 0, 0, 0.2)', border: 'none' }}
      />
      <Bar stackId="a" dataKey="GQA" fill="#5E35B1" />
      <Bar stackId="a" dataKey="Speed Audit" fill="#00897B" />
    </BarChart>
  );
}

export const ManagerSummaryDataColumns: TableColumn[] = [
  {
    id: 'name',
    header: 'Name',
    cell: function ManagerName(props: any) {
      const managers = props.row.original.managers;
      if (managers.length === 0) {
        return null;
      } else if (managers.length === 1) {
        const { name, email, id } = props.row.original.managers[0];
        return (
          <Link to={buildRoute(Routes.ExpertManagement, { user: id })} className="flex flex-col">
            <div>{name}</div>
            <div className="text-xs">{email}</div>
            <div className="text-xs">{id}</div>
          </Link>
        );
      } else {
        const { id } = props.row.original.managers[0];
        return (
          <Link to={buildRoute(Routes.ExpertManagement, { user: id })} className="flex flex-col">
            {managers.map((m: any) => {
              const { name, email } = m;
              return (
                <div key={name} className="flex flex-col gap-0">
                  <div>{name}</div>
                  <div className="text-xs">{email}</div>
                </div>
              );
            })}
          </Link>
        );
      }
    },
  },
  {
    id: 'squadType',
    header: 'Squad Type',
    cell: function SquadTypeCell(props: any) {
      const squadType = props.getValue() ?? '';
      return SQUAD_TYPE_TO_NAME_MAP[squadType as SquadType] ?? 'N/A';
    },
  },
  {
    id: 'teamSize',
    header: 'Total Team Size',
  },
  {
    id: 'numActive',
    header: 'Active Members',
    cell: function ActiveMembers(props: any) {
      const { teamSize, numActive } = props.row.original as any;
      return `${teamSize > 0 ? Math.round((100 * numActive) / teamSize) : 0}%`;
    },
  },
  {
    id: 'slackJoinPercentage',
    header: 'Slack %',
    cell: function SlackJoinPercentage(props: any) {
      const { slackJoinPercentage } = props.row.original as any;
      return `${slackJoinPercentage != null ? `${slackJoinPercentage}%` : '-'}`;
    },
  },
  {
    id: 'teamAttempts',
    header: 'Team Attempts',
    cell: function Attempts(props: CellFnProps) {
      const { teamTasks, teamReviews } = props.row.original as any;
      return (
        <div>
          <div className="text-sm">
            <span className="font-medium">{teamTasks}</span>
          </div>
          <div className="text-xs">
            {teamTasks - teamReviews} attempts / {teamReviews} reviews
          </div>
        </div>
      );
    },
  },
  {
    id: 'teamHours',
    header: 'Team Hours Worked',
    cell: Rounded,
  },
  {
    id: 'gqaSort',
    header: 'Quality Control Average',
    cell: (props: any) => Stats(props.row.original.gqaStats, { decimalPlaces: 2 }),
  },
  {
    id: 'speedAuditSort',
    header: 'Audit Average',
    cell: (props: any) => Stats(props.row.original.speedAuditStats, { decimalPlaces: 2 }),
  },
  {
    id: 'averageSbqPercentage',
    header: 'Average SBQ %',
    cell: function SbqPercentage(props: any) {
      const { averageSbqPercentage } = props.row.original as any;
      return `${
        averageSbqPercentage != null ? `${Math.round(averageSbqPercentage * 10) / 10}%` : '-'
      }`;
    },
  },
  {
    id: 'averageFractalGuardian',
    header: 'Average Fractal',
    headerTooltip: 'Score ranges from 1-5',
    cell: function FractalGuardianScore(props: any) {
      const { averageFractalGuardian } = props.row.original;
      if (!averageFractalGuardian) {
        return '-';
      }
      return `${Math.round(averageFractalGuardian * 10) / 10}`;
    },
  },
  {
    id: 'distributions',
    header: 'Quality Score Distribution',
    cell: BarDistribution,
  },
];

interface ManagerTableProps {
  title?: string;
  data: object[] | null;
  isLoading: boolean;
  reloadData: () => void;
  columns?: TableColumn[];
}

export const ManagerTable: React.FC<ManagerTableProps> = ({
  title = 'Leaders',
  data,
  isLoading,
  reloadData,
  columns = ManagerSummaryDataColumns,
}: ManagerTableProps) => {
  const {
    rowSelectionState,
    setRowSelectionState,
    selectedRows,
    moveManagersModalOpen,
    setMoveManagersModalOpen,
    handleUnfollowUsers,
    handleAssignNewManagers,
    handleSetManagerToNull,
    isModifying,
  } = useExpertActionsState(data, reloadData);
  const canModify = useCanUserModifyCommunity();

  const displayState = useTableColumnDisplayOrder('manager-table', columns);
  const { isColumnVisible, toggleVisibility } = displayState;

  useEffect(() => {
    if (data) {
      for (const column of AUTO_HIDEABLE_COLUMNS) {
        if (
          data.every((row: any) => row[column] === null || row[column] === undefined) &&
          isColumnVisible(column)
        ) {
          toggleVisibility(column);
        } else if (
          !data.every((row: any) => row[column] === null || row[column] === undefined) &&
          !isColumnVisible(column)
        ) {
          toggleVisibility(column);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, toggleVisibility]);

  // hack due to scaleui not supporting sortFn for columns
  const enrichedData = useMemo(
    () =>
      (data ?? [])
        .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
        .map((row: any) => ({
          ...row,
          teamAttempts: row.teamTasks + row.teamReviews,
          gqaSort: row.gqaStats?.average ?? 0,
          speedAuditSort: row.speedAuditStats?.average ?? 0,
        })),
    [data],
  );

  const handleManageUsersMenu = useCallback(
    (option: any) => {
      switch (option) {
        case 'unfollow':
          handleUnfollowUsers();
          break;
        case 'moveusers':
          handleAssignNewManagers();
          break;
        case 'removemanager':
          handleSetManagerToNull();
          break;
      }
    },
    [handleUnfollowUsers, handleAssignNewManagers, handleSetManagerToNull],
  );

  return (
    <>
      <MoveToNewManagerModal
        open={moveManagersModalOpen}
        onClose={() => setMoveManagersModalOpen(false)}
        expertIds={selectedRows.map((row: any) => row.id)}
      />
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Typography.Subtitle2>{title}</Typography.Subtitle2>
          <div className="flex items-center gap-2">
            {canModify && (
              <SelectMenu
                disabled={selectedRows.length === 0}
                variant="minimal"
                options={[
                  {
                    id: 'none',
                    name: 'Manage Selected Users',
                  },
                  {
                    id: 'unfollow',
                    name: 'Remove Users',
                  },
                  {
                    id: 'moveusers',
                    name: 'Assign New Leader',
                  },
                  {
                    id: 'removemanager',
                    name: 'Move to Top Level',
                  },
                ]}
                selectedOptionId="none"
                setSelected={handleManageUsersMenu}
              />
            )}
            <TableColumnDisplayOrderConfig context={displayState} />
          </div>
        </div>
        {isLoading || isModifying ? (
          <LoadingTable columns={columns} rowHeight={2} numRows={2} />
        ) : (
          <Table
            columns={displayState.displayColumns}
            data={enrichedData}
            rowSelectionState={rowSelectionState}
            onRowSelectionStateChange={s => setRowSelectionState(s)}
            enableRowSelection={canModify}
            defaultPageSize={50}
            freezeFirstColumn
          />
        )}
      </div>
    </>
  );
};
